import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import {
  AuthProvider,
  DarkModeProvider,
  configureApi,
  configureErrorToaster,
  initPostHog,
  posthog,
} from '@halo/web-lib';
import { PostHogProvider } from 'posthog-js/react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { TooltipProvider } from './components/ui/Tooltip';
import { DateRangeProvider } from './context/DateRangeContext';
import { DashboardPreferencesProvider } from './context/DashboardPreferencesContext';
import { CurrencyProvider } from './context/CurrencyProvider';
import { Toaster, toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { App } from './App';
import './index.css';

const ReactQueryDevtools = React.lazy(() =>
  import('@tanstack/react-query-devtools').then((mod) => ({
    default: mod.ReactQueryDevtools,
  })),
);

// ---------- Sentry ----------
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  release: import.meta.env.VITE_BUILD_SHA ?? undefined,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    /AbortError/,
    /AbortController/,
    'Network request failed',
    'Failed to fetch',
    'Load failed',
    /ChunkLoadError/,
    'Loading chunk',
  ],
  denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i],
});

// ---------- PostHog ----------
initPostHog(import.meta.env.VITE_POSTHOG_KEY ?? '', {
  host: import.meta.env.VITE_POSTHOG_HOST,
  app: 'dashboard',
});

// ---------- API ----------
configureApi(import.meta.env['VITE_GATEWAY_URL'] ?? 'http://localhost:3001');
configureErrorToaster((code, message) => {
  if (code === 'impersonation_expired') {
    window.location.href = 'https://monitoring.growhalo.ai/stores';
    return;
  }
  toast.error(message);
});

// ---------- React Query ----------
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      Sentry.captureException(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      Sentry.captureException(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes (matches backend Redis cache TTL of 300s)
      gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
      retry: 2, // Retry transient failures twice
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
      refetchOnWindowFocus: false, // Analytics data doesn't change that fast
    },
    mutations: {
      retry: 0, // Don't retry mutations by default
    },
  },
});

// ---------- Render ----------
const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <BrowserRouter future={{ v7_startTransition: true }}>
        <NuqsAdapter>
          <QueryClientProvider client={queryClient}>
            <DarkModeProvider>
              <AuthProvider>
                <CurrencyProvider>
                  <TooltipProvider>
                    <DateRangeProvider>
                      <DashboardPreferencesProvider>
                        <App />
                        <Toaster
                          richColors
                          position="top-right"
                          toastOptions={{
                            duration: 5000,
                          }}
                        />
                      </DashboardPreferencesProvider>
                    </DateRangeProvider>
                  </TooltipProvider>
                </CurrencyProvider>
              </AuthProvider>
            </DarkModeProvider>
            {import.meta.env.DEV && (
              <Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            )}
          </QueryClientProvider>
        </NuqsAdapter>
      </BrowserRouter>
    </PostHogProvider>
  </React.StrictMode>,
);
